import { useState, useEffect } from 'react';
import { FiX, FiCalendar, FiUser, FiAlertTriangle } from 'react-icons/fi';
import { IoBedOutline } from 'react-icons/io5';
import { GiBroom } from 'react-icons/gi';
import { roomsAPI, incidentsAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import { Modal } from '../../design-system';
import clsx from 'clsx';

const RoomHistoryDialog = ({ room, onClose }) => {
  const [orders, setOrders] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'incidents'

  useEffect(() => {
    loadData();
  }, [room.id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [ordersData, incidentsData] = await Promise.all([
        roomsAPI.getHistory(room.id),
        incidentsAPI.getByRoom(room.id)
      ]);

      setOrders(ordersData);
      setIncidents(incidentsData);
    } catch (error) {
      console.error('Error loading room history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendiente: {
        label: 'Pendiente',
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      },
      en_proceso: {
        label: 'En Proceso',
        className: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      completado: {
        label: 'Completado',
        className: 'bg-green-100 text-green-800 border-green-200'
      }
    };

    const config = statusConfig[status] || statusConfig.pendiente;

    return (
      <span className={clsx(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border',
        config.className
      )}>
        {config.label}
      </span>
    );
  };

  const getIncidentStatusBadge = (status) => {
    if (status === 'open') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
          Abierta
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
        Cerrada
      </span>
    );
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="4xl"
      showCloseButton={false}
      className="max-h-[90vh] flex flex-col"
    >
      <Modal.Header>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Habitación {room.number}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Historial de órdenes de limpieza e incidencias
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('orders')}
              className={clsx(
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'orders'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              Órdenes de Limpieza ({orders.length})
            </button>
            <button
              onClick={() => setActiveTab('incidents')}
              className={clsx(
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'incidents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              Incidencias ({incidents.length})
            </button>
          </nav>
        </div>
      </Modal.Header>

      {/* Content */}
      <Modal.Body className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" text="Cargando historial..." />
            </div>
          ) : (
            <>
              {activeTab === 'orders' && (
                <div className="space-y-4">
                  {orders.length === 0 ? (
                    <div className="text-center py-12">
                      <FiCalendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-700 mb-2">
                        No hay órdenes de limpieza
                      </h3>
                      <p className="text-gray-500">
                        Esta habitación no tiene órdenes de limpieza registradas
                      </p>
                    </div>
                  ) : (
                    orders.map((order) => (
                      <div
                        key={order.id}
                        className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                      >
                        {/* Fila única con todos los datos */}
                        <div className="flex items-center justify-between text-xs">
                          {/* Fecha */}
                          <div className="flex items-center space-x-2 min-w-[100px]">
                            <FiCalendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="font-medium text-gray-900 text-sm">
                              {formatDate(new Date(order.date))}
                            </span>
                          </div>

                          {/* Tipo de limpieza */}
                          <div className="flex items-center space-x-1 text-gray-600 min-w-[120px]">
                            <GiBroom className="h-5 w-5 text-green-500 flex-shrink-0" />
                            <span className="font-medium">{order.cleaning_type_obj?.name || order.cleaning_type_name || 'N/A'}</span>
                          </div>

                          {/* Configuración de cama */}
                          <div className="flex items-center space-x-1 text-gray-600 min-w-[100px]">
                            <IoBedOutline className="h-4 w-4 flex-shrink-0" />
                            <span>{order.bed_disposition?.name || order.bed_disposition_name || 'N/A'}</span>
                          </div>

                          {/* Personal */}
                          <div className="flex items-center space-x-1 text-gray-600 min-w-[120px]">
                            <FiUser className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span>{order.cleaning_staff?.name || 'Sin asignar'}</span>
                          </div>

                          {/* Checkout */}
                          <div className="flex items-center space-x-1 min-w-[90px]">
                            {order.checkout_timestamp ? (
                              <div className="flex items-center space-x-1 text-green-600">
                                <p className="text-[10px] leading-tight">Check-out</p>
                                <span className="font-medium">
                                  {new Date(order.checkout_timestamp).toLocaleTimeString('es-ES', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1 text-gray-400">
                                <p className="text-[10px] leading-tight">Check-out</p>
                                <span>--:--</span>
                              </div>
                            )}
                          </div>

                          {/* Limpieza */}
                          <div className="flex items-center space-x-1 min-w-[90px]">
                            {order.cleaning_timestamp ? (
                              <div className="flex items-center space-x-1 text-blue-600">
                                <p className="text-[10px] leading-tight">Limpieza</p>
                                <span className="font-medium">
                                  {new Date(order.cleaning_timestamp).toLocaleTimeString('es-ES', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1 text-gray-400">
                                <p className="text-[10px] leading-tight">Limpieza</p>
                                <span>--:--</span>
                              </div>
                            )}
                          </div>

                          {/* Estado badge */}
                          <div className="flex-shrink-0">
                            {getStatusBadge(order.cleaning_timestamp ? 'completado' : 'pendiente')}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'incidents' && (
                <div className="space-y-4">
                  {incidents.length === 0 ? (
                    <div className="text-center py-12">
                      <FiAlertTriangle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-700 mb-2">
                        No hay incidencias
                      </h3>
                      <p className="text-gray-500">
                        Esta habitación no tiene incidencias registradas
                      </p>
                    </div>
                  ) : (
                    incidents.map((incident) => (
                      <div
                        key={incident.id}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <FiAlertTriangle className="h-5 w-5 text-orange-500" />
                            <span className="font-medium text-gray-900">
                              {incident.title}
                            </span>
                          </div>
                          {getIncidentStatusBadge(incident.status)}
                        </div>

                        <p className="text-sm text-gray-600 mb-3">
                          {incident.description}
                        </p>

                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center space-x-4">
                            <div>
                              <span>Reportada por:</span>
                              <span className="ml-1 font-medium text-gray-700">
                                {incident.creator_name || 'Desconocido'}
                              </span>
                            </div>
                            <div>
                              <span>Fecha:</span>
                              <span className="ml-1 font-medium text-gray-700">
                                {new Date(incident.created_at).toLocaleString('es-ES', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>

                          {incident.status === 'closed' && incident.closed_at && (
                            <div>
                              <span>Cerrada:</span>
                              <span className="ml-1 font-medium text-gray-700">
                                {new Date(incident.closed_at).toLocaleString('es-ES', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              {incident.closer_name && (
                                <span className="ml-1">
                                  por {incident.closer_name}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
      </Modal.Body>
    </Modal>
  );
};

export default RoomHistoryDialog;
